import React, { useState, useEffect } from 'react';
import { makeStyles } from 'design-system/styles';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from '@mui/material';
import { Button } from 'design-system/Button';
import { AppBar, Box, MuiTabs, MuiTab, MobileStepper, MuiPopover, TextInput } from 'design-system';
import useToggle from 'react-use-v13/lib/useToggle';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import {
  LinterExample,
  NiceConfigLinter,
  LidarLinterSection,
  MlLinterSection,
  LinterDocsType,
  LinterConfig,
  DocsType,
  linters101,
  linterConfigGuide,
  lintersEmptyTemplate,
  UPDATE_LINTER_CONFIG_DOCS_URL,
  LidarLiteLinterSection,
} from '@scale/shared/types/corp/linterConfigDocs';
import { LinterNiceConfig, AddNiceConfigLinter, TaskTypeCheckbox } from './NiceLintersComponents';
import { AddLidarLinter } from './LidarLinterComponents';
import { AddMLConfigLinter } from './MLLintersComponents';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import dynamic from 'next/dynamic';
import { LidarLiteConfig } from './LidarLiteLinterComponent';
const ReactJsonViewBase = dynamic(() => import('react-json-view'), { ssr: false });

const copyToClipboard = function (
  data: string,
  {
    copyRaw = false,
  }: {
    copyRaw: boolean;
  },
): void {
  const value = copyRaw ? data : JSON.stringify(data);
  navigator.clipboard.writeText(value);
};

export const LinterJsonViewer: React.FC<IJSONViewProps> = props => {
  const enableClipboard = ({ src }: { src: any }) => {
    const serialized = typeof src === 'string' ? src : JSON.stringify(src);
    copyToClipboard(serialized, { copyRaw: true });
  };
  return <ReactJsonViewBase {...props} enableClipboard={enableClipboard} />;
};

const addNewLinter = (props: {
  docsType: DocsType;
  linterDocsJson: LinterDocsType;
  currentLinter: LinterConfig;
  isEdit?: boolean;
  onLinterUpdate: () => void;
  popupClose?: () => void;
}) => {
  const addNiceConfigProps = { ...props, currentLinter: props.currentLinter as NiceConfigLinter };
  const addLidarLinterProps = {
    ...props,
    currentLinter: props.currentLinter as LidarLinterSection,
  };
  const addMlLinterProps = { ...props, currentLinter: props.currentLinter as MlLinterSection };
  switch (props.docsType) {
    case DocsType.NiceConfigLinters:
      return <AddNiceConfigLinter {...addNiceConfigProps} />;
    case DocsType.LidarLinters:
      return <AddLidarLinter {...addLidarLinterProps} />;
    case DocsType.MLLinters:
      return <AddMLConfigLinter {...addMlLinterProps} />;
    default:
      return <AddNiceConfigLinter {...addNiceConfigProps} />;
  }
};

export const MenuTabs = (props: {
  NiceConfigChild: () => JSX.Element;
  LidarLiteLinters: () => JSX.Element;
  MLLinters?: () => JSX.Element;
  tabIndex: number;
  setTabIndex: (index: number) => void;
}) => {
  const { NiceConfigChild, LidarLiteLinters, tabIndex, setTabIndex } = props;
  const TabPanel = (props: { children?: React.ReactNode; index: any; value: any }) => {
    const { children, value, index, ...other } = props;
    return (
      <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} {...other}>
        {value === index && children}
      </div>
    );
  };
  const tabProps = (index: any) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };
  const handleChange = (_e: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <div style={{ marginTop: '-32px', marginRight: '-64px' }}>
      <AppBar position="static" style={{ marginLeft: '-32px' }}>
        <MuiTabs
          style={{ paddingLeft: '32px' }}
          value={tabIndex}
          onChange={handleChange}
          aria-label="tabs-menu"
        >
          <MuiTab label="Nice Config (Response) Linters" {...tabProps(0)} />
          <MuiTab label="Lidar Lite Linters" {...tabProps(1)} />
        </MuiTabs>
      </AppBar>
      <TabPanel value={tabIndex} index={0}>
        <NiceConfigChild />
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <LidarLiteLinters />
      </TabPanel>
    </div>
  );
};

export const SideBarAndLintersContainer = ({
  docsType,
  taskTypes,
  selectedTaskTypes,
  setSelectedTaskTypes,
  onTypeSelection,
  linterDocsJson,
  onLinterUpdate,
  selectedLinter,
}: {
  docsType: DocsType;
  taskTypes: string[];
  selectedTaskTypes: string[];
  setSelectedTaskTypes: React.Dispatch<React.SetStateAction<string[]>>;
  onTypeSelection: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  linterDocsJson: LinterDocsType;
  onLinterUpdate: () => void;
  selectedLinter: string;
}) => {
  const [searchInput, setSearchInput] = useState('');
  const classes = styles();
  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = e.target.value;
    setSearchInput(value);
  };
  const [niceConfigLinters, setNiceConfigLinters] = useState<NiceConfigLinter[]>(
    linterDocsJson?.niceConfigLinters ?? [],
  );
  const [lidarLiteLinters, setLidarLiteLinters] = useState<LidarLiteLinterSection[]>(
    linterDocsJson?.lidarLiteLinters ?? [],
  );

  return (
    <div className={classes.wrapper}>
      <div className={classes.left}>
        <LeftSideBarContainer
          linterDocsJson={linterDocsJson}
          docsType={docsType}
          taskTypes={taskTypes}
          selectedTaskTypes={selectedTaskTypes}
          setSelectedTaskTypes={setSelectedTaskTypes}
          onTypeSelection={onTypeSelection}
          onLinterUpdate={onLinterUpdate}
          onSearch={onSearch}
        />
      </div>
      <div className={classes.right}>
        <LintersContainer
          docsType={docsType}
          linterDocsJson={linterDocsJson}
          onLinterUpdate={onLinterUpdate}
          selectedLinter={selectedLinter}
          selectedTaskTypes={selectedTaskTypes}
          searchInput={searchInput}
          niceConfigLinters={niceConfigLinters}
          setNiceConfigLinters={setNiceConfigLinters}
          lidarLiteLinters={lidarLiteLinters}
          setLidarLiteLinters={setLidarLiteLinters}
        />
      </div>
    </div>
  );
};

const LeftSideBarContainer = ({
  docsType,
  taskTypes,
  selectedTaskTypes,
  setSelectedTaskTypes,
  onTypeSelection,
  linterDocsJson,
  onLinterUpdate,
  onSearch,
}: {
  docsType: DocsType;
  taskTypes: string[];
  selectedTaskTypes: string[];
  setSelectedTaskTypes: React.Dispatch<React.SetStateAction<string[]>>;
  onTypeSelection: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  linterDocsJson: LinterDocsType;
  onLinterUpdate: () => void;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const classes = styles();
  return (
    <>
      <div className={classes.navInnerWrapper}>
        <div style={{ marginTop: 20, paddingLeft: 4 }}>
          <h2 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Linter Filters</h2>
        </div>
        <div className={classes.navItem}>
          <TextInput
            onChange={onSearch}
            size="medium"
            placeholder="Search linter"
            fullWidth={true}
          />
        </div>
        <LinterPopover buttonText="Linters 101" markDownText={linters101[docsType]} />
        <LinterPopover buttonText="Linter Set up Info" markDownText={linterConfigGuide[docsType]} />
        {DocsType.LidarLiteLinters !== docsType && (
          <>
            <Button className={classes.popoverButton} type="primary">
              <a
                href="https://docs.google.com/document/d/1CnRxevbFI3jb8RioGhmYgUsjMv5YUqz7h3FsZRShbeg/edit?usp=sharing"
                style={{ color: 'white' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Guide to Default Linters
              </a>
            </Button>
            <AddLinterPopover
              linterDocsJson={linterDocsJson}
              onLinterUpdate={onLinterUpdate}
              docsType={docsType}
            />

            <DeleteLinterPopover
              linterDocsJson={linterDocsJson}
              onLinterUpdate={onLinterUpdate}
              docsType={docsType}
            />
          </>
        )}
        <div>
          {![DocsType.LidarLinters, DocsType.LidarLiteLinters].includes(docsType) && (
            <TaskTypeCheckbox
              taskTypes={taskTypes}
              selectedTaskTypes={selectedTaskTypes}
              setSelectedTaskTypes={setSelectedTaskTypes}
              onTypeSelection={onTypeSelection}
            />
          )}
        </div>
      </div>
    </>
  );
};

const LintersContainer = ({
  docsType,
  linterDocsJson,
  onLinterUpdate,
  selectedLinter,
  selectedTaskTypes,
  searchInput,
  niceConfigLinters,
  setNiceConfigLinters,
  lidarLiteLinters,
  setLidarLiteLinters,
}: {
  docsType: DocsType;
  linterDocsJson: LinterDocsType;
  onLinterUpdate: () => void;
  selectedLinter: string | undefined;
  selectedTaskTypes: string[];
  searchInput: string;
  niceConfigLinters: NiceConfigLinter[];
  setNiceConfigLinters: (linters: NiceConfigLinter[]) => void;
  lidarLiteLinters: LidarLiteLinterSection[];
  setLidarLiteLinters: (linters: LidarLiteLinterSection[]) => void;
  mlLinters?: MlLinterSection[];
  setMlLinters?: (linters: MlLinterSection[]) => void;
}) => {
  const getStyle = (selected: boolean) => {
    return selected
      ? { background: '#fafaff', borderRadius: '8px' }
      : { background: 'transparent', borderRadius: '8px' };
  };

  const sortLinters = (a: LinterConfig, b: LinterConfig) => a.name.localeCompare(b.name);
  const searchLinter = (linter: LinterConfig) =>
    linter.name.toLowerCase().includes(searchInput.toLocaleLowerCase());
  const filterByTaskType = (linter: NiceConfigLinter | MlLinterSection) =>
    !selectedTaskTypes.length || linter.allowedTaskTypes.some(t => selectedTaskTypes.includes(t));

  useEffect(() => {
    if (niceConfigLinters && niceConfigLinters.length) {
      setNiceConfigLinters(
        [...linterDocsJson.niceConfigLinters.sort(sortLinters)].filter(filterByTaskType),
      );
    }
    if (lidarLiteLinters && lidarLiteLinters.length) {
      setLidarLiteLinters([...linterDocsJson.lidarLiteLinters.sort(sortLinters)]);
    }
  }, []);

  useEffect(() => {
    if (
      linterDocsJson &&
      linterDocsJson.niceConfigLinters &&
      docsType === DocsType.NiceConfigLinters
    ) {
      setNiceConfigLinters(
        linterDocsJson.niceConfigLinters.filter(searchLinter).filter(filterByTaskType),
      );
    }
    if (
      linterDocsJson &&
      linterDocsJson.lidarLiteLinters &&
      docsType === DocsType.LidarLiteLinters
    ) {
      setLidarLiteLinters(linterDocsJson.lidarLiteLinters.filter(searchLinter));
    }
  }, [searchInput]);

  switch (docsType) {
    case DocsType.NiceConfigLinters:
      return (
        (niceConfigLinters && niceConfigLinters.length && (
          <div>
            {niceConfigLinters.map((linter, i) => (
              <LinterNiceConfig
                getStyle={getStyle}
                linterDocsJson={linterDocsJson}
                selected={linter.scriptName === selectedLinter}
                isPreview={false}
                onLinterUpdate={onLinterUpdate}
                linter={linter}
                key={i}
              />
            ))}
          </div>
        )) || <></>
      );
    case DocsType.LidarLiteLinters:
      return (
        (lidarLiteLinters && lidarLiteLinters.length && (
          <div>
            {lidarLiteLinters.map((linter, i) => (
              <LidarLiteConfig
                getStyle={getStyle}
                selected={linter.name === selectedLinter}
                linter={linter}
                key={i}
              />
            ))}
          </div>
        )) || <></>
      );

    default:
      return (
        (niceConfigLinters && niceConfigLinters.length && (
          <div>
            {niceConfigLinters.map((linter, i) => (
              <LinterNiceConfig
                getStyle={getStyle}
                linterDocsJson={linterDocsJson}
                selected={linter.scriptName === selectedLinter}
                isPreview={false}
                onLinterUpdate={onLinterUpdate}
                linter={linter}
                key={i}
              />
            ))}
          </div>
        )) || <></>
      );
  }
};

const AddLinterPopover = (props: {
  docsType: DocsType;
  linterDocsJson: LinterDocsType;
  onLinterUpdate: () => void;
}) => {
  const classes = styles();
  const { linterDocsJson, onLinterUpdate, docsType } = props;

  return (
    <PopupState variant="popover" popupId="add-new-linter-popover">
      {popupState => (
        <div>
          <Button className={classes.popoverButton} type="success" {...bindTrigger(popupState)}>
            <FontAwesomeIcon icon="plus" className={classes.popoverButtonPadding} />
            Add Linter Doc
          </Button>
          <MuiPopover
            className={classes.popoverAdd}
            {...bindPopover(popupState)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            marginThreshold={48}
          >
            <Box className={classes.popoverInner} p={0} style={{ marginBottom: 40 }}>
              {addNewLinter({
                linterDocsJson,
                onLinterUpdate,
                currentLinter: lintersEmptyTemplate[docsType],
                docsType,
              })}
            </Box>
          </MuiPopover>
        </div>
      )}
    </PopupState>
  );
};

const LinterPopover = (props: { buttonText: string; markDownText: string }) => {
  const classes = styles();
  const { buttonText, markDownText } = props;
  return (
    <PopupState variant="popover" popupId={`linter-popper-${buttonText.replace(' ', '-')}`}>
      {popupState => (
        <div>
          <Button className={classes.popoverButton} type="primary" {...bindTrigger(popupState)}>
            {buttonText}
          </Button>
          <MuiPopover
            className={classes.popoverLinter101}
            {...bindPopover(popupState)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box className={classes.popoverInner} p={0}>
              <SanitizedMarkdown text={markDownText} />
            </Box>
          </MuiPopover>
        </div>
      )}
    </PopupState>
  );
};

const DeleteLinterPopover = (props: {
  docsType: DocsType;
  linterDocsJson: LinterDocsType;
  onLinterUpdate: () => void;
}) => {
  const classes = styles();
  const { linterDocsJson, onLinterUpdate, docsType } = props;

  return (
    <PopupState variant="popover" popupId="delete-linter-popover">
      {popupState => (
        <div>
          <Button className={classes.popoverButton} type="error" {...bindTrigger(popupState)}>
            <FontAwesomeIcon icon="trash" className={classes.popoverButtonPadding} />
            Delete Linter Doc
          </Button>
          <MuiPopover
            className={classes.popoverAdd}
            {...bindPopover(popupState)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          >
            <Box className={classes.popoverInner} p={0}>
              <DeleteLinter
                docsType={docsType}
                linterDocsJson={linterDocsJson}
                onLinterUpdate={onLinterUpdate}
              />
            </Box>
          </MuiPopover>
        </div>
      )}
    </PopupState>
  );
};

export const LintExamples = (props: { examples: LinterExample[] }) => {
  const { examples } = props;
  const [activeExample, setActiveExample] = useState<number>(0);
  const [isShowingRaw, toggleIsShowingRaw] = useToggle(false);
  const handleNext = () => {
    setActiveExample(prev => (prev < examples.length - 1 ? prev + 1 : prev));
  };
  const handleBack = () => {
    setActiveExample(prev => (prev > 0 ? prev - 1 : prev));
  };

  const classes = styles();
  return (
    <div>
      <MobileStepper
        steps={examples.length}
        position="static"
        variant="dots"
        activeStep={activeExample}
        nextButton={
          <a>
            <IconButton onClick={handleNext} style={{ color: 'var(--color-RemoBlue)' }}>
              <FontAwesomeIcon icon="arrow-right" size="xs" />
            </IconButton>
          </a>
        }
        backButton={
          <a>
            <IconButton onClick={handleBack} style={{ color: 'var(--color-RemoBlue)' }}>
              <FontAwesomeIcon icon="arrow-left" size="xs" />
            </IconButton>
          </a>
        }
      />
      <div>
        <h4 className={classes.lintExamples}>{examples[activeExample].name}</h4>
        <SanitizedMarkdown text={examples[activeExample].description || ''} />
        {isShowingRaw ? (
          <div
            style={{
              height: '100%',
              flex: 1,
              width: '100%',
            }}
          >
            <TextInput
              multiline={true}
              defaultValue={JSON.stringify(examples[activeExample].configJson, null, 4)}
              style={{ width: '100%', backgroundColor: 'white', borderRadius: '10px' }}
            />
          </div>
        ) : (
          <LinterJsonViewer
            src={examples[activeExample].configJson}
            name={false}
            theme="monokai"
            iconStyle="triangle"
            collapsed={10}
            collapseStringsAfterLength={60}
            groupArraysAfterLength={false}
            enableClipboard={true}
            displayObjectSize={false}
            displayDataTypes={false}
            style={{ borderRadius: '5px', padding: '10px' }}
          />
        )}
        <Button
          type="secondary"
          onClick={toggleIsShowingRaw}
          style={{ marginLeft: '10px', marginTop: '5px' }}
        >
          Toggle to {!isShowingRaw ? 'raw' : 'interactive'}
        </Button>
      </div>
    </div>
  );
};

export const LinterArguments = (props: {
  name: string;
  type: string;
  description: string;
  defaultValue?: string | number;
  backgroundColor?: string;
}) => {
  const { name, type, description, defaultValue, backgroundColor = '#fafaff' } = props;
  const argTypeStr = `(${type}${
    defaultValue || defaultValue !== '' ? `, default = ${defaultValue})` : `)`
  }`;
  return (
    <div
      style={{
        backgroundColor,
        padding: '10px',
        borderRadius: '10px',
        border: '#eee 2px solid',
        marginBottom: '10px',
      }}
    >
      <div style={{ display: 'inline-block', fontWeight: 'bold', fontSize: '1.2em' }}>{name}</div>
      {type && (
        <div style={{ display: 'inline-block', marginLeft: '5px', fontStyle: 'italic' }}>
          {argTypeStr}
        </div>
      )}
      {description && <div style={{ marginTop: '8px' }}>{description}</div>}
    </div>
  );
};

const DeleteLinter = (props: {
  docsType: DocsType;
  linterDocsJson: LinterDocsType;
  onLinterUpdate: () => void;
  popupClose?: () => void;
}) => {
  const { docsType, linterDocsJson, onLinterUpdate, popupClose } = props;
  const lintersList: string[] = linterDocsJson[docsType].map(lint => lint.name);
  const [selectedLinter, setSelectedLinter] = useState<string>(lintersList[0]);

  const deleteLint = async () => {
    const updatedJson = { ...linterDocsJson };
    const lintIndex = updatedJson[docsType].findIndex(lint => lint.name === selectedLinter);
    updatedJson[docsType].splice(lintIndex, 1);
    try {
      await apiFetch(null, UPDATE_LINTER_CONFIG_DOCS_URL, {
        method: 'POST',
        body: updatedJson,
      });
      if (popupClose) {
        popupClose();
      }
      onLinterUpdate();
    } catch (error) {
      defaultApiFetchErrorHandler(error as any, { handleHttp: true });
    }
  };
  return (
    <div>
      <div>
        <select
          multiple={false}
          onChange={e => setSelectedLinter(e.target.value)}
          value={selectedLinter}
          style={{ width: 300, height: 25, fontSize: 14, marginRight: 20 }}
        >
          {lintersList.map((linter, i) => (
            <option key={`deleteme${i}`}>{linter}</option>
          ))}
        </select>
        <Button type="error" onClick={deleteLint} style={{ marginTop: -5 }}>
          <FontAwesomeIcon icon="trash" style={{ marginRight: 5 }} /> Delete
        </Button>
      </div>
    </div>
  );
};

export const styles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    width: '100%',
    maxWidth: '1366',
    margin: '0 auto',
  },
  left: {
    width: 266,
    height: '100%',
    flexShrink: 0,
    padding: 12,
    position: 'fixed',
    zIndex: 1,
    marginTop: -30,
  },
  right: {
    marginLeft: 266,
    flexGrow: 1,
    padding: '24px 40px',
    width: 1100,
    // paddingTop: 50,
  },
  navInnerWrapper: {
    marginTop: 32,
    paddingBottom: 32,
    height: 'calc(100vh - 106px - 32px)',
    position: 'relative',
  },
  loadSpinner: {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '0.7px',
    margin: 0,
    padding: 4,
    marginTop: 16,
    width: '95%',
    whiteSpace: 'pre-line',
  },
  navItem: {
    display: 'block',
    color: 'black',
    padding: '6px 8px',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    borderRadius: 4,
    opacity: 0.65,
    transition: 'all 120ms ease-out',
    '&:hover': {
      opacity: 1,
    },
  },
  linterSectionBodyWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    maxWidth: 1100,
  },
  linterSectionBody: {
    display: 'inline-block',
    width: '90%',
    borderRadius: 8,
    marginRight: 32,
    marginTop: '-16px',
    flexShrink: 0,
  },
  taskTypeCheckbox: {
    columnCount: 6,
  },
  tripleColumnWrapper: {
    columnCount: 3,
  },
  tripleColumn: {
    margin: 6,
  },
  doubleColumnWrapper: {
    margin: 6,
    display: 'grid',
    gridTemplateColumns: '13fr 1fr',
    gridGap: '6px',
  },
  widerColumn: {
    gridColumn: '1',
  },
  thinnerColumn: {
    gridColumn: '2',
    width: '75%',
    padding: '8px',
  },
  pill: {
    display: 'inline-block',
    margin: '10px 6px',
    padding: '3px 5px',
    backgroundColor: '#eee',
    borderRadius: '10px',
    fontFamily: theme.typography.fontFamilyMono,
    boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.15)',
  },
  lintExamples: {
    marginLeft: 8,
    marginTop: 5,
  },
  popoverButton: {
    marginLeft: 'auto',
    marginTop: 4,
    marginRight: 'auto',
    width: '100%',
  },
  popoverLinter101: {
    width: 840,
    zIndex: 100,
    maxHeight: '500px',
  },
  popoverAdd: {
    margin: 'auto',
    width: '100%',
    zIndex: 101,
    backdropFilter: 'brightness(45%) blur(8px)',
  },
  popoverInner: {
    margin: 16,
  },
  popoverButtonPadding: {
    marginRight: '6px',
  },
  select: {
    border: 'solid #878787 1px',
    borderRadius: '4px',
    color: '#808080',
    height: 32,
    minWidth: 300,
    paddingLeft: 14,
  },
  roundBorderBox: {
    border: 'solid #ccc 1px',
    padding: '0px 10px 20px 10px',
    borderRadius: '8px',
    marginTop: '20px',
  },
  boxShadow: {
    boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.15)',
  },
  roundBorder: {
    borderRadius: '8px',
  },
}));
